import { DatasetRef } from 'components/shared/Compare';
import {
  ComparisonContribution,
  ComparisonDatasetHeader,
  ComparisonIndicator,
  ComparisonResponse,
} from 'hooks/data/datasets/comparison/types';

/** A contributor, reduced to what the chart needs. `key` is positional — see `toContributions`. */
export type Contribution = { key: string; label: string; share: number; impact: number };

/** One impact category compared across the two datasets, amounts aligned by dataset position. */
export type ComparedIndicator = {
  key: string;
  category: string;
  indicator: string;
  unit: string;
  amounts: [number | undefined, number | undefined];
  contributions: [Contribution[], Contribution[]];
};

export type ComparedIndicators = {
  indicators: ComparedIndicator[];
  /**
   * The payload describes datasets we did not ask for, or results that can't be attributed to the
   * ones we did — showing its numbers would be a guess.
   */
  hasIdMismatch: boolean;
};

const toContributions = (contributions: ComparisonContribution[] = []): Contribution[] =>
  contributions.map((contribution, position) => ({
    // The payload does carry a flow `index`, but it is not known to be unique within one
    // indicator's contributions, and name+compartment demonstrably is not. The position in this
    // list is the only key that cannot collide.
    key: String(position),
    label: contribution.meta.name || contribution.meta.subcomp,
    share: contribution.relative_contribution,
    impact: contribution.impact,
  }));

/**
 * Finds the payload position that describes each requested dataset.
 *
 * Headers are matched on the full release — id, version and system model — never on the id alone.
 * Ids are release-scoped, so comparing a dataset against its own earlier release asks for the same id
 * twice and only the version tells the two apart. Matching the release also catches a payload that
 * resolved either dataset in the wrong one, which would otherwise render as a valid comparison.
 *
 * Returns `null` unless there are exactly two headers and each requested dataset matches a different
 * one. The header calls the dataset id `index`; only `results` uses the name `dataset_id`.
 */
const resolvePositions = (headers: ComparisonDatasetHeader[], pair: [DatasetRef, DatasetRef]): [number, number] | null => {
  if (headers.length !== 2) return null;

  const [positionA, positionB] = pair.map((ref) =>
    headers.findIndex(
      (header) => header.index === Number(ref.id) && header.version === ref.version && header.system_model === ref.systemModel
    )
  );
  if (positionA === -1 || positionB === -1 || positionA === positionB) return null;

  return [positionA, positionB];
};

/**
 * Results follow the order of the headers — the backend's contract, pinned by
 * test_comparison_across_versions in apollo-api — but carry only a `dataset_id`. So each result must
 * sit at a header with its id, and there must be one per header: the backend returns only indicators
 * scored for both datasets, so a missing result means a malformed payload, not absent data.
 *
 * That is as far as the payload lets the check go. When both datasets share an id, their results can
 * only be told apart by position, so their order is trusted here rather than verified.
 */
const resultsMatchHeaders = (indicator: ComparisonIndicator, headers: ComparisonDatasetHeader[]): boolean =>
  indicator.results.length === headers.length &&
  indicator.results.every((result, position) => result.dataset_id === headers[position].index);

const toComparedIndicator = (indicator: ComparisonIndicator, [positionA, positionB]: [number, number]): ComparedIndicator => {
  const [resultA, resultB] = [indicator.results[positionA], indicator.results[positionB]];

  return {
    key: `${indicator.category_name}|${indicator.indicator_name}`,
    category: indicator.category_name,
    indicator: indicator.indicator_name,
    unit: indicator.unit_name,
    amounts: [resultA.amount, resultB.amount],
    contributions: [toContributions(resultA.direct_contributions), toContributions(resultB.direct_contributions)],
  };
};

/**
 * Turns the wire payload into the shape the components render. Every positional assumption about the
 * response lives here, so a backend reshape lands in one tested function rather than in the JSX.
 *
 * All or nothing: if any header or indicator can't be attributed to the requested datasets, nothing is
 * rendered. A backend that got one indicator wrong may have got others wrong in ways that pass.
 */
export const buildComparedIndicators = (
  payload: ComparisonResponse | undefined,
  requested: [DatasetRef | undefined, DatasetRef | undefined]
): ComparedIndicators => {
  const [refA, refB] = requested;
  if (!payload || !refA || !refB) return { indicators: [], hasIdMismatch: false };

  const positions = resolvePositions(payload.datasets, [refA, refB]);
  if (!positions || !payload.indicators.every((indicator) => resultsMatchHeaders(indicator, payload.datasets))) {
    return { indicators: [], hasIdMismatch: true };
  }

  return { indicators: payload.indicators.map((indicator) => toComparedIndicator(indicator, positions)), hasIdMismatch: false };
};
